import { DeviceReading } from '../services/deviceApi';

export interface FilledReading
  extends Omit<Partial<DeviceReading>, 'parameterValue' | 'subIndex'> {
  recordedAt: string;
  parameterValue: number | null;
  subIndex: number | null;
  bucketIndex: number;
}

export interface FillBounds {
  startTime: number;
  endTime: number;
}

/**
 * Aligns sensor readings onto an evenly-spaced time grid by snapping each reading
 * to its nearest grid slot, rather than requiring an exact timestamp match.
 *
 * Each reading is assigned a `bucketIndex = round((t - startTime) / intervalMs)`.
 * Empty slots are filled with null-valued entries so series stay evenly spaced and,
 * when two series are filled against the same `bounds`, they share identical bucket
 * indices and can be merged positionally.
 *
 * Real readings keep their own `recordedAt` (so tooltips and downstream timestamp
 * lookups stay correct); only null fillers carry a synthetic grid timestamp.
 *
 * @param data Readings for a single parameter. Need not be sorted.
 * @param intervalMs Grid step in milliseconds.
 * @param bounds Optional explicit start/end. When omitted, derived from the data range.
 */
export function fillMissingIntervals(
  data: DeviceReading[],
  intervalMs: number,
  bounds?: FillBounds
): FilledReading[] {
  if ((!data || data.length === 0) && !bounds) return [];

  const sorted = (data ?? [])
    .slice()
    .sort(
      (a, b) =>
        new Date(a.recordedAt).getTime() - new Date(b.recordedAt).getTime()
    );

  const gridStart = bounds?.startTime ?? new Date(sorted[0].recordedAt).getTime();
  const gridEnd =
    bounds?.endTime ?? new Date(sorted[sorted.length - 1].recordedAt).getTime();
  const lastIndex = Math.max(0, Math.round((gridEnd - gridStart) / intervalMs));

  const byBucket = new Map<number, DeviceReading>();
  for (const reading of sorted) {
    const idx = Math.round(
      (new Date(reading.recordedAt).getTime() - gridStart) / intervalMs
    );
    if (idx < 0 || idx > lastIndex) continue;
    byBucket.set(idx, reading); // last-write-wins on same-bucket collisions
  }

  const template = sorted[0];
  const filled: FilledReading[] = [];
  for (let idx = 0; idx <= lastIndex; idx++) {
    const real = byBucket.get(idx);
    if (real) {
      filled.push({ ...real, bucketIndex: idx }); // keep real recordedAt
    } else {
      filled.push({
        ...(template ?? ({} as DeviceReading)),
        recordedAt: new Date(gridStart + idx * intervalMs).toISOString(),
        parameterValue: null,
        subIndex: null,
        bucketIndex: idx,
      });
    }
  }

  return filled;
}
